"use client";

import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type SVGProps,
} from "react";
import Link from "next/link";
import {
  getConsentSnapshot,
  getServerConsentSnapshot,
  OPEN_SETTINGS_EVENT,
  subscribeConsent,
  writeConsent,
} from "@/components/cookie-consent/consent";

const subscribeHydrated = () => () => {};

function XIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M6 6 18 18M18 6 6 18" />
    </svg>
  );
}

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden
        className={`text-[11px] font-semibold uppercase tracking-wide ${
          checked ? "text-brand-electric" : "text-on-surface-variant"
        }`}
      >
        {checked ? "On" : "Off"}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-electric ${
          checked ? "bg-brand-electric" : "bg-outline-variant"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform motion-reduce:transition-none ${
            checked ? "translate-x-[22px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </span>
  );
}

export function CookieManager() {
  const hydrated = useSyncExternalStore(
    subscribeHydrated,
    () => true,
    () => false,
  );
  const consent = useSyncExternalStore(
    subscribeConsent,
    getConsentSnapshot,
    getServerConsentSnapshot,
  );
  const [panelOpen, setPanelOpen] = useState(false);
  const [draftOptional, setDraftOptional] = useState(false);
  const [draftExceptions, setDraftExceptions] = useState(true);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useEffect(() => {
    window.kleipdataCookieConsent = consent;
  }, [consent]);

  useEffect(() => {
    function onOpen() {
      const c = getConsentSnapshot();
      setDraftOptional(c.optional);
      setDraftExceptions(c.exceptions);
      setPanelOpen(true);
    }
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!panelOpen) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPanelOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = previous;
    };
  }, [panelOpen]);

  function apply(choice: { optional: boolean; exceptions: boolean }) {
    writeConsent(choice);
    setPanelOpen(false);
  }

  function openPanel() {
    setDraftOptional(consent.optional);
    setDraftExceptions(consent.exceptions);
    setPanelOpen(true);
  }

  if (!hydrated) return null;

  const showBanner = !consent.presented && !panelOpen;

  return (
    <>
      {showBanner && (
        <div
          role="region"
          aria-label="Cookie choices"
          className="fixed inset-x-0 bottom-0 z-[70] px-3 pb-3 sm:px-4 sm:pb-4"
        >
          <div className="mx-auto flex max-w-[900px] flex-col gap-4 rounded-xl border border-white/10 bg-brand-navy p-4 text-white shadow-2xl sm:flex-row sm:items-center sm:gap-6 sm:p-5">
            <p className="flex-1 text-sm leading-6 text-white/80">
              We use strictly necessary cookies to run this site. With your
              permission we may also use optional technologies. Statistical and
              appearance technologies run under a legal exception unless you
              object.{" "}
              <Link
                href="/cookies"
                className="font-medium text-brand-cyan hover:underline"
              >
                Cookie Policy
              </Link>
            </p>
            <div className="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={() => apply({ optional: false, exceptions: true })}
                className="rounded-lg border border-white/25 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-cyan"
              >
                Reject optional
              </button>
              <button
                type="button"
                onClick={() => apply({ optional: true, exceptions: true })}
                className="rounded-lg bg-brand-cyan px-4 py-2 text-sm font-semibold text-brand-navy transition-colors hover:bg-[#00c2c2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-cyan"
              >
                Accept optional
              </button>
              <button
                type="button"
                onClick={openPanel}
                className="text-sm font-medium text-white/70 underline underline-offset-2 transition-colors hover:text-white"
              >
                Cookie settings
              </button>
            </div>
          </div>
        </div>
      )}

      {panelOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div
            className="absolute inset-0 bg-brand-navy/50"
            onClick={() => setPanelOpen(false)}
          />
          <div className="relative z-10 max-h-[92vh] w-full max-w-[520px] overflow-y-auto rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
            <div className="flex items-start justify-between gap-4">
              <h2 id={titleId} className="text-lg font-bold text-brand-navy">
                Cookie settings
              </h2>
              <button
                ref={closeRef}
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="Close cookie settings"
                className="-m-1 rounded p-1 text-on-surface-variant transition-colors hover:bg-[#f7f9fc] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-electric"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="mt-2 text-sm leading-6 text-on-surface-variant">
              Choose which technologies KleipData may use on this website.
              Strictly necessary technologies are always on. See the{" "}
              <Link
                href="/cookies"
                className="font-medium text-brand-electric hover:underline"
              >
                Cookie Policy
              </Link>{" "}
              for detail.
            </p>

            <ul className="mt-5 space-y-4">
              <li className="rounded-lg border border-outline-variant/70 p-4">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold text-brand-navy">
                    Strictly necessary
                  </span>
                  <span className="rounded bg-brand-navy/5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-navy">
                    Always on
                  </span>
                </div>
                <p className="mt-1.5 text-[13px] leading-6 text-on-surface-variant">
                  Needed for sign-in, security, load balancing and remembering
                  your cookie choice. These cannot be switched off.
                </p>
              </li>

              <li className="rounded-lg border border-outline-variant/70 p-4">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold text-brand-navy">
                    Statistical &amp; appearance
                  </span>
                  <Switch
                    checked={draftExceptions}
                    onChange={setDraftExceptions}
                    label="Allow statistical and appearance technologies"
                  />
                </div>
                <p className="mt-1.5 text-[13px] leading-6 text-on-surface-variant">
                  Aggregate statistics used to improve the site, and remembering
                  display preferences. These may run under a legal exception —
                  switch off to object.
                </p>
              </li>

              <li className="rounded-lg border border-outline-variant/70 p-4">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold text-brand-navy">
                    Optional
                  </span>
                  <Switch
                    checked={draftOptional}
                    onChange={setDraftOptional}
                    label="Allow optional technologies"
                  />
                </div>
                <p className="mt-1.5 text-[13px] leading-6 text-on-surface-variant">
                  Analytics that go beyond aggregate statistics, added
                  third-party functionality, and advertising. Only used with your
                  consent.
                </p>
              </li>
            </ul>

            <p className="mt-4 rounded-lg bg-[#f7f9fc] p-3 text-[12px] leading-5 text-on-surface-variant">
              KleipData does not currently load any third-party analytics or
              advertising technologies. Your choice is saved and applied
              automatically if that changes.
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-end gap-4">
              <button
                type="button"
                onClick={() => {
                  setDraftOptional(false);
                  setDraftExceptions(true);
                }}
                className="text-sm font-medium text-brand-electric hover:underline"
              >
                Reset to minimum
              </button>
              <button
                type="button"
                onClick={() =>
                  apply({
                    optional: draftOptional,
                    exceptions: draftExceptions,
                  })
                }
                className="rounded-lg bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-navy-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-electric"
              >
                Save settings
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
